/**
 * Facet Filters Component - Handles collection filtering functionality
 * Manages filter form state, URL updates, and AJAX product grid updates
 */

import { Component } from "./component-base.js";
import { ThemeBus } from "./events-bus.js";
import { EVENTS } from "./event-constants.js";

/**
 * Custom element for collection page filters with AJAX updates
 * @extends HTMLElement
 */

class ActiveFiltersComponent extends HTMLElement {
  setFilters(params) {
    this.innerHTML = '';

    // Helper to format major units (e.g., 500 => £500.00)
    const formatMoneyMajor = (amount) => {
      try {
        const currency = (window.Shopify && Shopify.currency && (Shopify.currency.active || Shopify.currency)) || undefined;
        const nf = new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'GBP' });
        return nf.format(Number(amount) || 0);
      } catch (_) {
        return '£' + (Number(amount) || 0).toFixed(2);
      }
    };

    // Build entries but skip empty values
    const entries = Array.from(params.entries()).filter(([name, value]) =>
      name.startsWith('filter.') && name !== 'sort_by' && value !== ''
    );

    // Price keys participate in visibility
    const minKey = 'filter.v.price.gte';
    const maxKey = 'filter.v.price.lte';
    const minVal = params.get(minKey);
    const maxVal = params.get(maxKey);

    const hasPrice = (minVal && String(minVal).trim() !== '') || (maxVal && String(maxVal).trim() !== '');
    if (entries.length === 0 && !hasPrice) {
      this.style.display = 'none';
      return;
    }

    this.style.display = '';
    if ((minVal && minVal !== '') || (maxVal && maxVal !== '')) {
      const tag = document.createElement('span');
      tag.className = `
        active-filter-tag 
        inline-flex items-center gap-2 px-3 py-3 leading-none text-2xs rounded-full bg-input-bg text-primary
      `.trim();

      let text = '';
      if (minVal && maxVal) {
        text = `<span class="font-bold">Price</span>: ${formatMoneyMajor(minVal)} - ${formatMoneyMajor(maxVal)}`;
      } else if (minVal) {
        text = `<span class=\"font-bold\">Price</span>: ${formatMoneyMajor(minVal)} +`;
      } else if (maxVal) {
        text = `<span class=\"font-bold\">Price</span>: Up to ${formatMoneyMajor(maxVal)}`;
      }

      tag.innerHTML = `
        <span>${text}</span>
        <button type="button" class="remove-filter" data-filter-name="${minKey}" data-alt-filter-name="${maxKey}" aria-label="Remove price filter">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      `;

      const removeButton = tag.querySelector('.remove-filter');
      removeButton.addEventListener('click', (e) => {
        e.preventDefault();
        // Clear URL params for price and trigger filters update
        const params = new URLSearchParams(window.location.search);
        params.delete(minKey);
        params.delete(maxKey);
        const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
        history.replaceState({}, '', newUrl);

        // Also clear any present inputs to keep UI in sync
        document.querySelectorAll(`[name="${minKey}"]`).forEach((el) => { el.value = ''; });
        document.querySelectorAll(`[name="${maxKey}"]`).forEach((el) => { el.value = ''; });

        // Notify listeners (FacetFilters listens to popstate to restore & fetch)
        window.dispatchEvent(new PopStateEvent('popstate'));
      });

      this.appendChild(tag);
    }

    // Render other non-price filters as individual tags (with group label)
    entries.forEach(([name, value]) => {
      if (name === minKey || name === maxKey) return; // handled above
      const tag = document.createElement('span');
      tag.className = `
        active-filter-tag 
        inline-flex items-center gap-2 px-6 py-4 leading-none text-2xs rounded-full bg-input-bg text-primary
      `.trim();
    
      const label = document.createElement('span');
      // Try to find a human-friendly label from the form
      const input = document.querySelector(`[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`);
      let valueLabel = input ? (input.closest('label')?.textContent || '').trim() : '';
      // Allow inputs to override chip text via data attributes (e.g., for toggles)
      if (input && input.dataset && input.dataset.chipLabel) {
        valueLabel = input.dataset.chipLabel;
      }
      let groupLabel = '';
      if (input) {
        const details = input.closest('details');
        const summarySpan = details ? details.querySelector('summary span') : null;
        groupLabel = (summarySpan?.textContent || '').trim();
        if (!groupLabel && input.dataset && input.dataset.groupLabel) {
          groupLabel = input.dataset.groupLabel;
        }
      }
      const finalText = `${groupLabel ? groupLabel + ': ' : ''}${valueLabel || value}`;
      if (groupLabel) {
        const strong = document.createElement('span');
        strong.className = 'font-bold';
        strong.textContent = groupLabel + ':';
        label.append(strong, ' ', valueLabel || value);
      } else {
        label.textContent = valueLabel || value;
      }
    
      const removeBtn = document.createElement('button');
      removeBtn.textContent = '×';
      removeBtn.className = `
        remove-filter-btn 
        ml-1 
        text-gray-500 
        hover:text-red-600 
        focus:outline-none
      `.trim();
    
      removeBtn.setAttribute('data-name', name);
      removeBtn.setAttribute('data-value', value);
      removeBtn.setAttribute('aria-label', `Remove ${finalText}`);
    
      tag.append(label, removeBtn);
      this.appendChild(tag);
    });
    
    // Add a "Clear all" control when there are any active entries
    if (entries.length > 0 || (minVal && minVal !== '') || (maxVal && maxVal !== '')) {
      const clearAll = document.createElement('button');
      clearAll.type = 'button';
      clearAll.textContent = 'Clear all';
      clearAll.className = 'ml-2 inline-flex items-center text-2xs underline text-gray-700 hover:text-accent';
      clearAll.addEventListener('click', (e) => {
        e.preventDefault();
        const current = new URLSearchParams(window.location.search);
        // Preserve context params
        const q = current.get('q');
        const type = current.get('type');
        const sortBy = current.get('sort_by');
        // Remove all filter.* keys
        Array.from(current.keys()).forEach((key) => {
          if (key.startsWith('filter.')) current.delete(key);
          if (key === 'page') current.delete(key);
        });
        if (q) current.set('q', q);
        if (type) current.set('type', type);
        if (sortBy) current.set('sort_by', sortBy);

        // Clear price range inputs to keep UI in sync
        const minKey = 'filter.v.price.gte';
        const maxKey = 'filter.v.price.lte';
        document.querySelectorAll(`[name="${minKey}"]`).forEach((el) => { el.value = ''; });
        document.querySelectorAll(`[name="${maxKey}"]`).forEach((el) => { el.value = ''; });

        const newUrl = current.toString() ? `${window.location.pathname}?${current}` : window.location.pathname;
        history.replaceState({}, '', newUrl);
        // Reset filter-selected-count displays so they show 0 immediately
        document.querySelectorAll('.filter-selected-count[data-filter-param]').forEach((el) => {
          el.textContent = '0';
        });
        // Let FacetFiltersFormComponent react via popstate
        window.dispatchEvent(new PopStateEvent('popstate'));
      });
      const wrapper = document.createElement('div');
      wrapper.className = 'inline-flex items-center';
      wrapper.appendChild(clearAll);
      this.appendChild(wrapper);
    }
    
  }

  getRemoveButtons() {
    return this.querySelectorAll('.remove-filter-btn');
  }
}

// Register early
if (!customElements.get('active-filters')) {
  customElements.define('active-filters', ActiveFiltersComponent);
}

class FacetFiltersFormComponent extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.activeFiltersEls = Array.from(document.querySelectorAll('active-filters'));

    if (!this.form) {
      // Form may not be parsed yet; watch for it
      const mo = new MutationObserver(() => {
        this.form = this.querySelector('form');
        if (this.form) {
          mo.disconnect();
          this.#initialize();
        }
      });
      mo.observe(this, { childList: true, subtree: true });
      return;
    }
  
    // Form exists, initialize immediately
    this.#initialize();
  }

  /**
   * After filter markup is replaced from an AJAX response, re-bind without treating
   * this as a cold load (avoids a second #fetchFilteredGrid while the URL already matches the grid).
   */
  reinitializeAfterAjaxSwap() {
    this.form = this.querySelector('form');
    this.activeFiltersEls = Array.from(document.querySelectorAll('active-filters'));
    if (!this.form) return;
    this._initialized = false;
    this.#initialize({ skipAutofetchFromUrl: true });
  }

  #initialize(options = {}) {
    const { skipAutofetchFromUrl = false } = options;
    if (this._initialized) return; // Prevent double initialization
    this._initialized = true;
      
    // 1. Bind event listeners
    this.#bindInitialEventListeners();

    // 2. Restore filter UI from URL
    this.#restoreFiltersFromUrl();
    this.#bindDetailsAccordionBehavior();
  
    // 3. Render active filters on initial load
    const params = new URLSearchParams(window.location.search);
      this.#ensureActiveFiltersReady().then((ready) => {
        if (window.__skipActiveFiltersSetOnce) {
          window.__skipActiveFiltersSetOnce = false;
          return;
        }
        if (ready) {
          this.#setActiveFilters(params);
          this.#bindRemoveEvents();
        }
      });
  
    // 4. Fetch matching products if URL has filters or sorting (not just page param)
    const hasFiltersOrSort = Array.from(params.keys()).some(key =>
      key.startsWith('filter.') || key === 'sort_by' || key === 'q' || key === 'type'
    );

    // Prevent duplicate fetches from multiple form instances on initial load
    if (!skipAutofetchFromUrl && hasFiltersOrSort && !window.__fetchInProgress) {
      this.#fetchFilteredGrid(params);
    }
  }
  

  #bindRemoveEvents() {
    this.#ensureActiveFiltersReady().then((ready) => {
      if (!ready) return;

      const buttons = Array.from(document.querySelectorAll('active-filters')).flatMap(el => Array.from(el.getRemoveButtons?.() || []));
      buttons.forEach(btn => {
        btn.addEventListener('click', (e) => {
        e.preventDefault();
        const name = btn.getAttribute('data-name');
        const value = btn.getAttribute('data-value');

        if (!name) return;

        // Try to uncheck the corresponding checkbox in the form first
        let checkbox;
        try {
          const selector = `[name="${CSS.escape(name)}"][value="${CSS.escape(value || '')}"]`;
          checkbox = this.form.querySelector(selector);
        } catch (_) {
          checkbox = null;
        }

        if (checkbox) {
          checkbox.checked = false;
          this.#handleFilterChange();
          return;
        }

        // Fallback: manipulate URL params directly (handles cases where input isn't present)
        const params = new URLSearchParams(window.location.search);
        const existing = params.getAll(name);
        if (existing.length > 0) {
          params.delete(name);
          existing.forEach(v => {
            if (value == null || value === '' || v !== value) params.append(name, v);
          });
        }
        // Preserve search context
        const q = params.get('q');
        const type = params.get('type');
        if (q) params.set('q', q);
        if (type) params.set('type', type);

        history.replaceState({}, '', `${window.location.pathname}?${params}`);
        this.#fetchFilteredGrid(params);
        this.#setActiveFilters(params);
        this.#restoreFiltersFromUrl();
        });
      });
    });
  }

  /**
   * Public method to trigger filter change (can be called externally)
   */
  triggerFilterChange() {
    this.#handleFilterChange();
  }

  /**
   * Handles filter form changes by updating URL and fetching new results
   */
  #handleFilterChange() {
    // Exclude empty price fields from FormData by temporarily disabling them
    const tempDisabled = [];
    ['filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
      const input = this.form.querySelector(`[name="${key}"]`);
      if (input && (input.value == null || String(input.value).trim() === '')) {
        if (!input.disabled) {
          input.disabled = true;
          tempDisabled.push(input);
        }
      }
    });

    const formData = new FormData(this.form);

    // Restore inputs after serialization
    tempDisabled.forEach((el) => { el.disabled = false; });
    
    // Collect sorting data - check both mobile (inside form) and desktop (outside form)
    if (!formData.get('sort_by')) {
      // Desktop sort is outside the form, so we need to find it separately
      // Look for any sort select that's NOT the mobile one and NOT inside the mobile drawer
      const allSortSelects = document.querySelectorAll('select[name="sort_by"]');
      const desktopSort = Array.from(allSortSelects).find(select => {
        return select.id !== 'sort-by-mobile' && !select.closest('mobile-filter-drawer-content');
      });
      if (desktopSort && desktopSort.value) {
        formData.set('sort_by', desktopSort.value);
      }
    }
    
    const params = new URLSearchParams(formData);
    ThemeBus.dispatchEvent(new CustomEvent(EVENTS.filtersApplying, { detail: { params: params.toString() } }));

    // Preserve search query context (search page) so filters don't drop the query
    const existingParams = new URLSearchParams(window.location.search);

    // Check if this is sorting-only (no filter changes)
    const hasFilters = Array.from(params.keys()).some(key => key.startsWith('filter.'));
    const hadFilters = Array.from(existingParams.keys()).some(key => key.startsWith('filter.'));
    const isSortingOnly = !hasFilters && !hadFilters && params.has('sort_by');

    // Disable image fade animation for sorting-only changes
    if (isSortingOnly) {
      window.__disableImageFadeOnce = true;
    }
    const q = existingParams.get('q');
    const type = existingParams.get('type');
    if (q) params.set('q', q);
    if (type) params.set('type', type);

    // Normalize price params: drop keys when inputs are blank or invalid
    ['filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
      const raw = params.get(key);
      if (raw == null) return;
      const val = String(raw).trim();
      // Drop empty, non-number, or zero values (treat 0 as unset)
      if (val === '' || Number.isNaN(Number(val)) || Number(val) === 0) {
        params.delete(key);
      }
    });
    
    history.replaceState({}, '', `${window.location.pathname}?${params}`);
    ThemeBus.dispatchEvent(new CustomEvent(EVENTS.filtersApplied, { detail: { params: params.toString(), status: 'optimistic' } }));
    // Update chips immediately, then fetch grid and re-bind remove listeners
    this.#setActiveFilters(params);
    this.#fetchFilteredGrid(params);
    this.#bindRemoveEvents();
  }

  /**
   * Gets the dynamic section ID from the shopify-section wrapper element
   * @returns {string} The section ID without the 'section-' prefix
   */
  #getSectionId() {
    // Find the shopify-section element that wraps this filter form
    let sectionElement = this.closest('.shopify-section') || this.form?.closest('.shopify-section');
    
    // If not found, look for the main collection section specifically
    if (!sectionElement) {
      // Look for the main section (contains the product grid)
      sectionElement = document.querySelector('#ProductGridContainer')?.closest('.shopify-section') || 
                     document.querySelector('[id*="__main"]') ||
                     document.querySelector('.shopify-section');
    }
    
    if (sectionElement && sectionElement.id) {
      // Strip the 'section-' prefix from the ID
      // e.g., 'shopify-section-template--26025869312339__main' becomes 'template--26025869312339__main'
      const sectionId = sectionElement.id.replace(/^shopify-section-/, '');
      return sectionId;
    }
    
    // Fallback to 'main' if no section ID found (since collection template uses 'main' section)
    return 'main';
  }

  // Update all active-filters instances with current params
  #setActiveFilters(params) {
    const els = Array.from(document.querySelectorAll('active-filters'));
    els.forEach((el) => {
      try { el.setFilters?.(params); } catch (_) {}
    });
  }

  /**
   * Updates the "X Selected" count display for a specific filter
   * When faceted nav is enabled, active values can be links (data-faceted-active-filter-option)
   * instead of checkboxes, so we count both.
   * @param {string} filterParam - The filter parameter name (e.g., "filter.p.tag")
   */
  #updateFilterSelectedCount(filterParam) {
    if (!this.form) return;
    
    // Count checked checkboxes for this filter
    let selectedCount = this.form.querySelectorAll(
      `input[name="${filterParam}"]:checked`
    ).length;
    
    // When faceted nav is enabled, also count active faceted filter options (links, not checkboxes)
    const facetedActiveCount = this.form.querySelectorAll(
      `[data-faceted-active-filter-option][data-filter-param="${CSS.escape(filterParam)}"]`
    ).length;
    selectedCount += facetedActiveCount;
    
    // Find the count display element and update it
    const countEl = this.form.querySelector(
      `.filter-selected-count[data-filter-param="${filterParam}"]`
    );
    
    if (countEl) {
      countEl.textContent = selectedCount;
    }
  }

  /**
   * Fetches filtered product grid via AJAX and updates the DOM
   * @param {URLSearchParams} params - The filter parameters
   */
  #fetchFilteredGrid(params) {
    const sectionId = this.#getSectionId();
    const url = `${window.location.pathname}?section_id=${sectionId}&${params}`;
    
    // Prevent duplicate fetches from multiple form instances
    window.__fetchInProgress = true;

    // Prevent stale updates by tracking the most recent request
    this._lastFetchToken = (this._lastFetchToken || 0) + 1;
    const currentToken = this._lastFetchToken;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch filtered results: ${res.status} ${res.statusText}`);
        return res.text();
      })
      .then((html) => {
        // Skip if a newer request finished
        if (currentToken !== this._lastFetchToken) return;

        const newDom = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = newDom.querySelector('#ProductGridContainer');
        const currentGrid = document.querySelector('#ProductGridContainer');
        
        if (currentGrid) {
          if (newGrid) {
            currentGrid.innerHTML = newGrid.innerHTML;

            // Update filter states (disable unavailable options)
            /* this.#updateFilterStates(newDom); */




/* ADDED BY HS */

            document.querySelectorAll('facet-filters-form').forEach((currentFilter) => {
              const id = currentFilter.id;
              const newFilter = id
                ? newDom.querySelector(`facet-filters-form#${CSS.escape(id)}`)
                : newDom.querySelector('facet-filters-form');
              if (newFilter) {
                currentFilter.innerHTML = newFilter.innerHTML;
                if (typeof currentFilter.reinitializeAfterAjaxSwap === 'function') {
                  currentFilter.reinitializeAfterAjaxSwap();
                }
              }
            });

/* END OF HS SECTION */



            // Dispatch gridUpdated event to trigger lazy loading (centralized in lazy-image.js)
            ThemeBus.dispatchEvent(new Event(EVENTS.gridUpdated));
          } else {
            // No grid returned (e.g., no results) → render empty state if template present
            const emptyTpl = document.getElementById('FiltersEmptyTemplate');
            currentGrid.innerHTML = emptyTpl ? emptyTpl.innerHTML : '';
            // Wire up Clear Filters link
            const clearBtn = document.getElementById('FiltersEmptyClear');
            if (clearBtn) {
              clearBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const current = new URLSearchParams(window.location.search);
                const q = current.get('q');
                const type = current.get('type');
                const sortBy = current.get('sort_by');
                Array.from(current.keys()).forEach((key) => {
                  if (key.startsWith('filter.')) current.delete(key);
                  if (key === 'page') current.delete(key);
                });
                if (q) current.set('q', q);
                if (type) current.set('type', type);
                if (sortBy) current.set('sort_by', sortBy);
                const newUrl = current.toString() ? `${window.location.pathname}?${current}` : window.location.pathname;
                history.replaceState({}, '', newUrl);
                window.dispatchEvent(new PopStateEvent('popstate'));
              });
            }
            ThemeBus.dispatchEvent(new CustomEvent(EVENTS.filtersNoResults, { detail: { params: params.toString() } }));
          }

          // Update product count (if present)
          this.#updateProductCount(newDom);

          // Notify other components via ThemeBus
          ThemeBus.dispatchEvent(new Event(EVENTS.filtersUpdated));
          ThemeBus.dispatchEvent(new CustomEvent(EVENTS.filtersApplied, { detail: { params: params.toString(), status: 'done' } }));
        }

        // Clear fetch lock
        window.__fetchInProgress = false;
      })
      .catch((error) => {
        console.error('Filter fetch failed:', error);
        // Clear fetch lock on error too
        window.__fetchInProgress = false;
      });
  }

  /**
   * Updates the product count display with the new count from AJAX response
   * @param {Document} newDom - The new DOM from the AJAX response
   */
  #updateProductCount(newDom) {
    const newProductCount = newDom.querySelector('[data-product-count]');
    const currentProductCounts = document.querySelectorAll('[data-product-count]');
    
    if (newProductCount && currentProductCounts.length > 0) {
      // Update all product count elements on the page
      currentProductCounts.forEach((currentProductCount, index) => {
        currentProductCount.innerHTML = newProductCount.innerHTML;
      });
    }
  }

  /**
   * Updates filter input states (enable/disable) based on available products
   * @param {Document} newDom - The new DOM from the AJAX response
   */


/* COMMENTED OUT BY HS */

/*

  #updateFilterStates(newDom) {
    // Get all filter inputs from the AJAX response
    const newFilterInputs = newDom.querySelectorAll('[data-filter-count]');

    newFilterInputs.forEach(newInput => {
      const filterName = newInput.getAttribute('name');
      const filterValue = newInput.getAttribute('value');
      const filterCount = parseInt(newInput.getAttribute('data-filter-count') || '0');
      const isActive = newInput.checked;

      // Find the corresponding input on the current page
      // Need to check both desktop and mobile forms
      const currentInputs = document.querySelectorAll(`[name="${CSS.escape(filterName)}"][value="${CSS.escape(filterValue)}"]`);

      currentInputs.forEach(currentInput => {
        const currentCount = parseInt(currentInput.getAttribute('data-filter-count') || '0');

        // Only update if count has changed
        if (currentCount !== filterCount) {
          // Update the count attribute
          currentInput.setAttribute('data-filter-count', filterCount);

          // Update disabled state (but keep active filters enabled so they can be unchecked)
          const shouldDisable = filterCount === 0 && !currentInput.checked;
          currentInput.disabled = shouldDisable;

          // Update the label styling
          const label = currentInput.closest('label');
          if (label) {
            if (shouldDisable) {
              label.classList.add('opacity-50', 'cursor-not-allowed');
              label.classList.remove('cursor-pointer');
            } else {
              label.classList.remove('opacity-50', 'cursor-not-allowed');
              label.classList.add('cursor-pointer');
            }

            // Update the label text span
            const labelText = label.querySelector('span:not(.text-gray-500)');
            if (labelText) {
              if (shouldDisable) {
                labelText.classList.add('line-through');
              } else {
                labelText.classList.remove('line-through');
              }
            }

            // Update the count display
            const countSpan = label.querySelector('.text-gray-500');
            if (countSpan) {
              countSpan.textContent = `(${filterCount})`;
            }
          }
        }
      });
    });
  }

  */

/* END OF HS COMMENTED SECTION */

  /**
   * Restores filter checkboxes and select elements based on current URL parameters
   */
  #restoreFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    // Uncheck all checkboxes first
    this.form.querySelectorAll("input[type=checkbox]").forEach((el) => el.checked = false);

    // Clear price range inputs first
    const minKey = 'filter.v.price.gte';
    const maxKey = 'filter.v.price.lte';
    this.form.querySelectorAll(`[name="${minKey}"], [name="${maxKey}"]`).forEach((el) => { el.value = ''; });

    for (const [name, value] of params.entries()) {
      // Handle checkboxes
      const input = this.form.querySelector(`[name="${name}"][value="${value}"]`);
      if (input) input.checked = true;
      
      // Handle price range inputs
      if (name === minKey || name === maxKey) {
        const priceInput = this.form.querySelector(`[name="${name}"]`);
        if (priceInput) {
          priceInput.value = value;
        }
      }
      
      // Handle sort_by select - update all sort selects (both desktop and mobile)
      if (name === 'sort_by') {
        document.querySelectorAll('select[name="sort_by"]').forEach(select => {
          const optionExists = Array.from(select.options).some(opt => opt.value === value);
          if (optionExists) {
            select.value = value;
            delete select.dataset.userSelected;
          }
        });
      }
    }
    
    // Refresh "X Selected" for every filter (covers faceted active options when enable_faceted_nav is on)
    this.form.querySelectorAll('.filter-selected-count[data-filter-param]').forEach((el) => {
      const param = el.getAttribute('data-filter-param');
      if (param) this.#updateFilterSelectedCount(param);
    });
    
    // Set default sort for mobile form if no sort in URL and user hasn't selected anything
    if (!params.has('sort_by') && this.form.id === 'mobile-filter-form') {
      const mobileSortSelect = this.form.querySelector('select[name="sort_by"]');
      if (mobileSortSelect) {
        const userSelected = mobileSortSelect.dataset.userSelected === 'true';
        const currentValue = mobileSortSelect.value;
        
        if (!userSelected && (!currentValue || currentValue === '')) {
          // Prefer 'manual' (featured), fall back to first option
          const manualOption = Array.from(mobileSortSelect.options).find(opt => opt.value === 'manual');
          if (manualOption) {
            mobileSortSelect.value = 'manual';
          } else if (mobileSortSelect.options.length > 0) {
            mobileSortSelect.value = mobileSortSelect.options[0].value;
          }
        }
      }
    }
  }

  /**
   * Bind initial event listeners for filter form, sort select, and history
   */
  #bindInitialEventListeners() {
    if (!this.form) return;

    // Bind form change listener
    this._formChangeHandler = (event) => {
      // Don't trigger immediate filter changes for mobile filter drawer
      // The mobile drawer will apply filters when "Apply Filters" button is clicked
      if (this.form.id === 'mobile-filter-form') {
        return; // Skip immediate application for mobile drawer
      }
        this.#handleFilterChange();
    };
    this.form.addEventListener("change", this._formChangeHandler);

    // Bind per-filter reset buttons
    this.form.addEventListener("click", (e) => {
      const resetBtn = e.target.closest('.filter-reset-btn');
      if (!resetBtn) return;
      e.preventDefault();
      const filterParam = resetBtn.dataset.filterParam;
      if (!filterParam) return;
      
      // Uncheck all checkboxes for this filter parameter
      this.form.querySelectorAll(`input[name="${filterParam}"]`).forEach((input) => {
        if (input.type === 'checkbox' || input.type === 'radio') {
          input.checked = false;
        } else {
          input.value = '';
        }
      });
      
      // For price range filters, also clear the min/max fields
      if (filterParam.includes('price')) {
        this.form.querySelectorAll(`input[name="${filterParam}.gte"], input[name="${filterParam}.lte"]`).forEach((input) => {
          input.value = '';
        });
      }
      
      // Update the selected count display
      this.#updateFilterSelectedCount(filterParam);
      
      this.#handleFilterChange();
    });

    // Update selected count when checkboxes change
    this.form.addEventListener("change", (e) => {
      const input = e.target;
      if (input.type === 'checkbox' && input.name.startsWith('filter.')) {
        this.#updateFilterSelectedCount(input.name);
      }
    });

    // Bind popstate listener (only once per instance)
    if (!this._popstateHandler) {
      this._popstateHandler = () => {
      this.#restoreFiltersFromUrl();
      
        // Only fetch if URL has actual filters/sort (not just page param)
      const params = new URLSearchParams(window.location.search);
        const hasFiltersOrSort = Array.from(params.keys()).some(key =>
          key.startsWith('filter.') || key === 'sort_by' || key === 'q' || key === 'type'
        );

        // Prevent duplicate fetches from multiple form instances
        if (hasFiltersOrSort && !window.__fetchInProgress) {
          this.#fetchFilteredGrid(params);
        }

      this.#setActiveFilters(params);
      this.#bindRemoveEvents();
      };
      window.addEventListener("popstate", this._popstateHandler);
    }
  }

  // Close sibling filter popovers when one opens (horizontal UI)
  // Also closes all filter details when clicking outside
  #bindDetailsAccordionBehavior() {
    if (!this.form) return;
    if (this._detailsClickOutsideHandler) {
      document.removeEventListener('click', this._detailsClickOutsideHandler, true);
    }
    const container = this.form.querySelector('filter-set') || this.form;
    const detailsList = container.querySelectorAll('details');
    if (!detailsList.length) return;
    
    // Close sibling details when one opens
    detailsList.forEach((el) => {
      el.addEventListener('toggle', () => {
        if (!el.open) return;
        detailsList.forEach((other) => {
          if (other !== el) other.open = false;
        });
      });
    });

    // Close all filter details when clicking outside
    this._detailsClickOutsideHandler = (event) => {
      // Check if click is inside any filter details element
      const clickedDetails = event.target.closest('details');
      const clickedSummary = event.target.closest('summary');
      
      // If clicking on a summary, let the native toggle behavior handle it
      // The toggle event listener above will handle closing siblings
      if (clickedSummary && clickedDetails) {
        return;
      }
      
      // If clicking inside a details element content (but not the summary), don't close
      // This allows users to interact with filter options without closing
      if (clickedDetails && clickedDetails.open) {
        return;
      }
      
      // Otherwise, close all open filter details
      detailsList.forEach((details) => {
        if (details.open) {
          details.open = false;
        }
      });
    };
    
    // Use capture phase to handle clicks before they bubble
    document.addEventListener('click', this._detailsClickOutsideHandler, true);
  }

  /**
   * Ensures the <active-filters> custom element is present and upgraded
   * @returns {Promise<boolean>} true if ready
   */
  async #ensureActiveFiltersReady() {
    if (!this.activeFiltersEl) {
      this.activeFiltersEl = document.querySelector('active-filters');
    }
    if (!this.activeFiltersEl) return false;
    // Try immediate upgrade if the definition exists
    try {
      if (customElements.get('active-filters')) {
        customElements.upgrade?.(this.activeFiltersEl);
      }
    } catch (_) {}
    if (typeof this.activeFiltersEl.setFilters === 'function') return true;
    // Wait a microtask and a frame to allow upgrade after DOM replacement
    await Promise.resolve();
    await new Promise((r) => requestAnimationFrame(r));
    try {
      if (customElements.get('active-filters')) {
        customElements.upgrade?.(this.activeFiltersEl);
      } else {
        await customElements.whenDefined('active-filters');
        customElements.upgrade?.(this.activeFiltersEl);
      }
    } catch (_) {}
    // Re-select in case the element reference changed after swap
    this.activeFiltersEl = document.querySelector('active-filters');
    return !!(this.activeFiltersEl && typeof this.activeFiltersEl.setFilters === 'function');
  }

}

export async function initFilters() {
  if (!customElements.get("facet-filters-form")) {
    customElements.define("facet-filters-form", FacetFiltersFormComponent);

    // Manually trigger connectedCallback for elements that were in the DOM before definition
    setTimeout(() => {
      const elements = document.querySelectorAll('facet-filters-form');
      elements.forEach((el) => {
        if (el.connectedCallback) {
          el.connectedCallback();
        }
      });
    }, 0);

    // Attach a single global listener for sort changes (outside the form)
    // This prevents multiple listeners from firing when there are multiple facet-filters-form elements
    document.addEventListener("change", (event) => {
      // Check if this is a sort select and NOT the mobile one
      if (event.target.matches && event.target.matches('select[name="sort_by"]') && event.target.id !== 'sort-by-mobile') {
        // Only handle desktop sort changes (mobile sort is handled by its own form listener)
        // Find the desktop form (not the mobile one which is inside mobile-filter-drawer-content)
        const allForms = document.querySelectorAll('facet-filters-form');
        const desktopForm = Array.from(allForms).find(form => {
          // Desktop form is NOT inside mobile-filter-drawer-content
          return !form.closest('mobile-filter-drawer-content');
        });

        if (desktopForm && desktopForm.triggerFilterChange) {
          desktopForm.triggerFilterChange();
        }
      }
    });
  }
}

// Auto-initialize when module loads
initFilters();
